import type { Therapy } from "./types";

/**
 * Therapy explainers, part one. Original Recovery Trauma™ wording.
 * Psychoeducation only — nothing here is therapy, diagnosis or treatment.
 */
export const THERAPIES_PART_ONE: Therapy[] = [
  {
    slug: "emdr",
    name: "Eye Movement Desensitisation & Reprocessing",
    abbrev: "EMDR",
    badge: "Trauma Processing",
    group: "trauma-processing",
    tagline:
      "A structured trauma therapy that pairs brief attention to a distressing memory with left-right (bilateral) stimulation. It aims to make the memory feel less charged, without you having to describe it in detail.",
    evidence: "established",
    evidenceSummary:
      "Recommended for PTSD in NICE, WHO and APA guidance, with a large randomised-trial base in single-incident adult PTSD.",
    whatIsIt: [
      "EMDR is an eight-phase, structured psychotherapy developed for distress linked to specific memories.",
      "Rather than talking a memory through at length, you hold parts of it in mind in short sets while doing something that moves your attention side to side — eye movements, alternating taps, or alternating sounds.",
      "The aim is not to forget what happened. It is for the memory to become something you can recall without your body reacting as though it were happening now.",
    ],
    howItWorks: [
      "Your therapist first spends time on stability and resourcing, so you have ways to settle before anything difficult is approached.",
      "You then identify a target: an image from the memory, the belief about yourself it carries, the feeling, and where you notice it in the body.",
      "In short sets of bilateral stimulation you notice whatever comes — images, thoughts, sensations — and then briefly report it. Sets repeat until the memory feels less disturbing.",
      "Finally a more useful belief about yourself is strengthened, and the body is checked for leftover tension.",
    ],
    theory: [
      "EMDR is built on the Adaptive Information Processing model: the idea that overwhelming experiences can be stored in a raw, unintegrated form, still carrying the original images, beliefs and sensations.",
      "In that model, bilateral stimulation while the memory is briefly held in mind helps the brain finish the processing it could not complete at the time.",
      "The exact mechanism is still debated. One leading explanation is working-memory taxation — doing two things at once reduces the vividness and emotional charge of the recalled image.",
    ],
    session: [
      "Early sessions are history-taking and preparation: what you want to work on, and what helps you settle.",
      "A processing session usually opens with a check-in and a grounding exercise, then targets one memory.",
      "You are asked to rate distress from 0 to 10, and how true a positive belief feels, so change is tracked rather than assumed.",
      "Sessions are usually closed deliberately — even if processing is incomplete — with containment and grounding before you leave.",
    ],
    techniques: [
      "Calm/safe place installation and other resourcing exercises",
      "Target identification: image, negative belief, emotion, body location",
      "Sets of bilateral stimulation (eye movements, tactile taps, alternating audio)",
      "Subjective Units of Distress (SUD) and Validity of Cognition (VOC) ratings",
      "Cognitive interweaves when processing stalls",
      "Body scan and closure/containment procedures",
    ],
    usedFor: [
      "PTSD following single or multiple traumatic events",
      "Distress connected to specific memories — accidents, assault, medical events, loss",
      "Sometimes used alongside treatment for anxiety, phobias, grief or chronic pain, where the evidence is thinner",
    ],
    trauma: [
      "EMDR is one of the front-line psychological treatments for PTSD in adults and is recommended in UK, US and WHO guidance.",
      "It is often chosen by people who find it hard to describe what happened out loud, because detailed verbal narration is not required.",
    ],
    complexTrauma: [
      "For complex trauma the standard approach is a longer, phase-based one: stabilisation and skills first, memory processing later, integration after.",
      "Rushing into processing without enough regulation capacity can increase flashbacks, dissociation or shutdown. A therapist experienced in complex trauma and dissociation should assess this before starting.",
      "Some people work with dissociation-specific preparation for months before any memory is targeted — that is normal practice, not a failure.",
    ],
    duration: [
      "Single-incident PTSD: often around 6–12 sessions.",
      "Multiple events or childhood trauma: frequently much longer, sometimes a year or more, with extended preparation.",
      "Sessions are usually 60–90 minutes, because processing needs time to open and close safely.",
    ],
    experience: [
      "During sets, people describe images, memories, sensations or emotions surfacing and shifting quickly.",
      "Distress often rises before it falls. Many people feel tired after a session, and some notice vivid dreams or continued processing over the following days.",
      "You stay awake, aware and in control throughout — you can stop a set at any point.",
    ],
    benefits: [
      "Reduction in intrusive memories, flashbacks and nightmares for many people with PTSD",
      "Less need to narrate the event in detail",
      "Often relatively brief for single-incident trauma",
      "Change in the felt sense of a memory, not only in what you think about it",
    ],
    limitations: [
      "Not a good fit while someone is in immediate danger, in acute crisis, or without enough stabilisation",
      "Can temporarily increase distress between sessions",
      "Requires a properly trained clinician; quality of training varies widely",
      "Evidence in complex/childhood trauma is more limited than in single-incident adult PTSD",
      "Not a substitute for medical care, and does not remove real ongoing stressors",
    ],
    evidenceBase: [
      "Multiple randomised controlled trials and meta-analyses support EMDR for adult PTSD, with effects broadly comparable to trauma-focused CBT.",
      "NICE (NG116) recommends EMDR for adults with PTSD; the WHO and the International Society for Traumatic Stress Studies also list it as a recommended treatment.",
      "The evidence is strongest for single-incident adult trauma. For C-PTSD, children, and for non-PTSD uses, the research is smaller and less consistent.",
      "Debate continues about whether the eye movements themselves add benefit beyond the exposure and structure of the protocol.",
    ],
    whoProvides: [
      "A registered mental health professional (psychologist, psychotherapist, psychiatrist, mental health nurse, clinical social worker) with accredited EMDR training.",
      "In the UK, look for EMDR UK accreditation alongside HCPC, BPS, BACP or UKCP registration; internationally, EMDRIA or EMDR Europe accreditation.",
    ],
    selfHelp: [
      "EMDR memory processing is not a self-help technique and should not be attempted alone from videos or apps.",
      "What you can safely practise between sessions is the preparation side: grounding, orienting to the present, breathing, and resourcing.",
      "Bilateral stimulation used only for calming (slow alternating taps, no memory targeted) is used by some people as a settling exercise — stop if it brings material up.",
    ],
    questions: [
      "What EMDR training and accreditation do you hold, and with which body?",
      "How much experience do you have with complex or childhood trauma, and with dissociation?",
      "How will we decide I'm ready to begin processing?",
      "What happens if I become overwhelmed mid-session, or between sessions?",
      "How will we measure whether this is helping?",
    ],
    faq: [
      {
        q: "What is EMDR therapy?",
        a: "EMDR (Eye Movement Desensitisation and Reprocessing) is an eight-phase structured psychotherapy for distress linked to specific memories. You hold part of a memory in mind in short sets while doing something that moves your attention side to side, until recalling it no longer sets off the same bodily reaction.",
      },
      {
        q: "What happens in an EMDR session?",
        a: "Early sessions cover history and preparation, including ways to settle. A processing session opens with a check-in and grounding, targets one memory, and runs short sets of bilateral stimulation with brief reporting in between. Sessions are closed deliberately with containment, even if processing is unfinished.",
      },
      {
        q: "Does EMDR work, and how quickly?",
        a: "EMDR is recommended for PTSD in NICE, WHO and APA guidance, with a large trial base in single-incident adult PTSD. Single-incident trauma sometimes shifts within a handful of sessions; complex or repeated trauma usually needs a much longer stabilising phase first, and no honest therapist will promise a number.",
      },
      {
        q: "Is EMDR safe for complex trauma or CPTSD?",
        a: "It can be, with a therapist trained in complex trauma and a phased approach that prioritises stability before processing. Going too fast can overwhelm, so pacing, resourcing and the ability to stop matter more here than anywhere else.",
      },
      {
        q: "Do I have to talk about what happened in EMDR?",
        a: "Not in detail. You identify an image, the belief it carries, the feeling and where you notice it in the body, then report briefly what comes up. Many people choose EMDR precisely because it does not require a full account.",
      },
      {
        q: "Can you do EMDR on yourself?",
        a: "No. Self-administered bilateral stimulation is not EMDR and can leave difficult material open with nothing to close it. Bilateral sounds or slow eye movement can be used for general settling, but memory processing belongs with a trained practitioner.",
      },
    ],
    resources: [
      { label: "Grounding toolkit", to: "/grounding", blurb: "Return to the present before and after difficult work." },
      { label: "Window of Tolerance", to: "/learn/window-of-tolerance", blurb: "Understand your own readiness for processing." },
      { label: "Trigger Journal", to: "/trigger-journal", blurb: "Track what activates you between sessions." },
    ],
    references: [
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
      {
        label: "Guidelines for the management of conditions specifically related to stress",
        source: "World Health Organization",
        url: "https://www.who.int/publications/i/item/9789241505406",
      },
      {
        label: "PTSD treatment guideline",
        source: "American Psychological Association",
        url: "https://www.apa.org/ptsd-guideline",
      },
    ],
  },

  {
    slug: "tf-cbt",
    name: "Trauma-Focused Cognitive Behavioural Therapy",
    abbrev: "TF-CBT",
    badge: "Trauma Processing",
    group: "trauma-processing",
    tagline:
      "A structured, components-based therapy — originally developed for children and young people — combining coping skills, gradual processing of the trauma story, and caregiver involvement.",
    evidence: "established",
    evidenceSummary:
      "One of the strongest evidence bases for trauma in children and adolescents; the adult trauma-focused CBT family is also guideline-recommended.",
    whatIsIt: [
      "TF-CBT is a short-term, structured treatment for post-traumatic stress in children, teenagers and their caregivers, delivered in defined components.",
      "It blends cognitive behavioural skills, gradual exposure to the trauma memory, and family work, so that the young person is not left to carry the material alone.",
      "The adult equivalents — trauma-focused CBT protocols such as cognitive therapy for PTSD — share the same logic: skills, then processing, then meaning.",
    ],
    howItWorks: [
      "Work is paced across components, often summarised as PRACTICE: psychoeducation and parenting, relaxation, affective regulation, cognitive coping, trauma narrative, in-vivo mastery, conjoint sessions and enhancing safety.",
      "Skills come before the story. Nothing about the trauma is approached until there are ways to manage the feelings it brings.",
      "The trauma narrative is built gradually, a piece at a time, and then re-read until it can be held without overwhelming distress.",
    ],
    theory: [
      "Avoidance keeps post-traumatic distress alive: what is never approached never gets updated.",
      "Meanings formed at the time of the trauma — 'it was my fault', 'nowhere is safe' — continue to shape feeling and behaviour until they are examined.",
      "A supportive caregiver who can tolerate hearing the story is itself a therapeutic factor for young people.",
    ],
    session: [
      "Sessions are typically weekly, around 60–90 minutes, often split between the young person and the caregiver.",
      "Early sessions teach relaxation, feelings-identification and cognitive coping using worksheets, games or drawing.",
      "Middle sessions build the narrative in small steps — writing, drawing, or recording a chapter at a time.",
      "Later sessions share the narrative with the caregiver and focus on safety and future planning.",
    ],
    techniques: [
      "Psychoeducation about trauma responses",
      "Relaxation and breathing skills",
      "Emotion identification and regulation work",
      "Cognitive coping — noticing and testing unhelpful thoughts",
      "Gradual trauma narrative development",
      "In-vivo mastery of avoided but safe reminders",
      "Conjoint child–caregiver sessions and safety planning",
    ],
    usedFor: [
      "PTSD and post-traumatic symptoms in children and adolescents",
      "Trauma-related depression, anxiety, shame and behavioural difficulties",
      "Sexual abuse, physical abuse, domestic violence exposure, traumatic bereavement",
    ],
    trauma: [
      "TF-CBT was designed for trauma from the start, so the whole protocol is organised around traumatic memory and its meanings.",
      "It is one of the most strongly recommended treatments for PTSD in young people internationally.",
    ],
    complexTrauma: [
      "Adapted versions exist for complex trauma, with a longer skills phase and more attention to relationship, attachment and safety before narrative work.",
      "Where a young person is still living with ongoing abuse or instability, safety and protection come before any processing.",
      "For adults with childhood complex trauma, phase-based trauma-focused CBT is common — often considerably longer than the standard protocol.",
    ],
    duration: [
      "Standard protocol: roughly 8–16 weekly sessions.",
      "Complex presentations: frequently 25 sessions or more.",
    ],
    experience: [
      "Many young people are anxious about the narrative phase and find it far more manageable than expected because it is built in small pieces.",
      "Distress commonly increases briefly around narrative work and then decreases.",
      "Caregivers often describe the conjoint session as the hardest and most important part.",
    ],
    benefits: [
      "Well-evidenced reduction in PTSD symptoms, depression and behavioural problems in young people",
      "Structured and time-limited",
      "Involves the caregiver, so support continues outside the room",
      "Skills learned are transferable well beyond the trauma work",
    ],
    limitations: [
      "Requires a safe, non-abusive caregiver to be involved — not always possible",
      "The structured format doesn't suit everyone",
      "Not appropriate while a child remains in an unsafe situation",
      "Ongoing instability, active substance dependence or acute risk may need addressing first",
    ],
    evidenceBase: [
      "Multiple randomised controlled trials support TF-CBT for post-traumatic stress in children and adolescents, including after sexual abuse and traumatic bereavement.",
      "NICE recommends trauma-focused CBT for children, young people and adults with PTSD.",
      "Cochrane reviews of psychological therapies for children exposed to trauma report benefit for trauma-focused CBT, while noting variability in trial quality.",
    ],
    whoProvides: [
      "Registered clinicians — clinical psychologists, CBT therapists, child psychotherapists, social workers or psychiatrists — with specific TF-CBT training and supervision.",
      "In the UK, look for BABCP accreditation alongside core professional registration.",
    ],
    selfHelp: [
      "The coping components (psychoeducation, relaxation, naming feelings) can be practised independently and are genuinely useful.",
      "Building a trauma narrative alone, without support, can be destabilising and is best done with a professional.",
    ],
    questions: [
      "Are you trained in the full TF-CBT model, and do you receive supervision in it?",
      "How will you decide when we are ready for the narrative phase?",
      "How will my family be involved, and what if a caregiver can't take part?",
      "What if my child becomes very distressed between sessions?",
    ],
    resources: [
      { label: "Learn Hub: CBT", to: "/learn/cbt", blurb: "The core cognitive behavioural skills this builds on." },
      { label: "Recovery Journal", to: "/journal", blurb: "A private place to write, at your own pace." },
      { label: "Breathing tools", to: "/breathing", blurb: "Regulation skills to practise between sessions." },
    ],
    references: [
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
      {
        label: "PTSD treatment guideline",
        source: "American Psychological Association",
        url: "https://www.apa.org/ptsd-guideline",
      },
      {
        label: "Psychological therapies for children exposed to trauma",
        source: "Cochrane Library",
        url: "https://www.cochranelibrary.com/",
      },
    ],
  },

  {
    slug: "cpt",
    name: "Cognitive Processing Therapy",
    abbrev: "CPT",
    badge: "Trauma Processing",
    group: "trauma-processing",
    tagline:
      "A 12-session trauma therapy focused on the beliefs trauma leaves behind — about safety, trust, power, esteem and intimacy — rather than on reliving the event.",
    evidence: "established",
    evidenceSummary:
      "A first-line PTSD treatment in US clinical guidelines, supported by numerous randomised controlled trials including in veterans and survivors of sexual assault.",
    whatIsIt: [
      "CPT is a manualised cognitive therapy for PTSD, usually delivered in 12 structured sessions.",
      "Its focus is on 'stuck points' — the conclusions drawn during or after the trauma that keep distress in place.",
      "There is a version with a written account of the trauma and a version without; both are effective, which makes CPT an option for people who don't want to write out what happened.",
    ],
    howItWorks: [
      "You learn how thoughts, feelings and events connect, then start catching your own stuck points on worksheets.",
      "Each stuck point is examined with structured questions: what is the evidence, what am I assuming, is there another way to read this?",
      "Later sessions work through five themes trauma commonly disrupts — safety, trust, power and control, esteem, and intimacy.",
    ],
    theory: [
      "CPT draws on social-cognitive theory: recovery stalls when new information can't be integrated with existing beliefs.",
      "People often either 'assimilate' (change the facts to protect the old belief — 'it must have been my fault') or 'over-accommodate' ('I can never trust anyone').",
      "The aim is balanced accommodation: beliefs that account for what happened without distorting the whole world.",
    ],
    session: [
      "Sessions are weekly or twice-weekly, around 50–60 minutes, and follow the manual closely.",
      "Each session reviews practice worksheets, introduces one new skill, and sets the next assignment.",
      "Practice between sessions is a core part of the treatment, not an optional extra.",
    ],
    techniques: [
      "ABC (event–belief–consequence) worksheets",
      "Stuck point log",
      "Challenging questions and patterns-of-problematic-thinking worksheets",
      "Impact statement, written at the start and again at the end",
      "Optional written trauma account",
      "Themed modules on safety, trust, power/control, esteem and intimacy",
    ],
    usedFor: [
      "PTSD in adults, including military, sexual assault, accident and refugee populations",
      "Trauma-related guilt, shame and self-blame",
      "Often used where depression and PTSD occur together",
    ],
    trauma: [
      "CPT was built for PTSD and is particularly well suited to trauma dominated by self-blame, guilt or shattered assumptions about the world.",
      "Because the written account is optional, it can suit people who find detailed exposure intolerable.",
    ],
    complexTrauma: [
      "CPT has been used and studied in people with multiple traumas and childhood abuse histories, with encouraging results.",
      "With complex trauma, more sessions, more stabilisation and more attention to the therapeutic relationship are common adaptations.",
      "Severe dissociation may need addressing before the cognitive work can hold.",
    ],
    duration: [
      "Standard course: 12 sessions, sometimes delivered over as few as 4–6 weeks in intensive formats.",
      "Extended courses are used where trauma is complex or symptoms remain.",
    ],
    experience: [
      "People often describe CPT as demanding but clear — there is homework, and progress is visible on paper.",
      "Guilt and shame frequently intensify briefly before shifting.",
      "The final impact statement, compared with the first, is often the moment change becomes obvious.",
    ],
    benefits: [
      "Strong, well-replicated symptom reduction in PTSD",
      "Time-limited and transparent",
      "Directly addresses self-blame and shame",
      "Can be delivered without a detailed trauma narrative",
    ],
    limitations: [
      "Structured and homework-heavy — not for everyone",
      "Less focus on body-based and relational aspects of trauma",
      "Acute risk, active dependence or severe dissociation may need attention first",
      "Requires a clinician properly trained in the protocol",
    ],
    evidenceBase: [
      "CPT is 'strongly recommended' in the APA PTSD guideline and in the joint VA/DoD guideline, based on multiple high-quality randomised trials.",
      "Trials show benefits maintained at long-term follow-up, including in survivors of sexual assault and in military populations.",
      "Comparative trials generally show CPT, prolonged exposure and EMDR performing similarly overall.",
    ],
    whoProvides: [
      "Licensed or registered mental health professionals with formal CPT training and consultation.",
      "Look for core registration (HCPC, BPS, BACP, UKCP, or equivalent) plus CPT-specific training.",
    ],
    selfHelp: [
      "The core idea — noticing a stuck point and asking what the evidence really is — can be practised in a journal.",
      "The full protocol, especially the trauma account, is meant to be delivered with a trained clinician.",
    ],
    questions: [
      "Do you deliver CPT with or without the written trauma account, and can I choose?",
      "How much homework should I expect each week?",
      "What is your training and consultation background in CPT?",
      "How will we handle it if my distress increases at first?",
    ],
    resources: [
      { label: "Shadow Work Journal", to: "/shadow-work", blurb: "Gentle work with self-blame and the beliefs underneath." },
      { label: "Learn Hub: CBT", to: "/learn/cbt", blurb: "The thought–feeling–behaviour foundations." },
      { label: "Shame-based trauma", to: "/learn/shame-based-trauma", blurb: "Why self-blame is so persistent after trauma." },
    ],
    references: [
      {
        label: "PTSD treatment guideline",
        source: "American Psychological Association",
        url: "https://www.apa.org/ptsd-guideline",
      },
      {
        label: "VA/DoD clinical practice guideline for PTSD",
        source: "US Department of Veterans Affairs",
        url: "https://www.healthquality.va.gov/guidelines/mh/ptsd/",
      },
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
    ],
  },

  {
    slug: "somatic-experiencing",
    name: "Somatic Experiencing",
    abbrev: "SE",
    badge: "Body-Based",
    group: "body-based",
    tagline:
      "A body-oriented approach that works with sensation, activation and settling in small doses, rather than retelling the trauma story.",
    evidence: "emerging",
    evidenceSummary:
      "Widely used and increasingly researched, but the trial base is much smaller than for EMDR, CPT, PE or TF-CBT — treat it as promising rather than proven.",
    whatIsIt: [
      "Somatic Experiencing is a body-focused approach developed by Peter Levine, working with the physical residue of overwhelming experience.",
      "Attention is placed on sensation — tightness, heat, trembling, the impulse to move — rather than on narrating events.",
      "Work is done in very small amounts, moving deliberately between activation and settling.",
    ],
    howItWorks: [
      "You are guided to notice a small amount of activation, then to return to something neutral or settling — a rhythm often called pendulation.",
      "Working in small doses (titration) is intended to keep the nervous system inside a range it can manage.",
      "Incomplete survival responses — the running, pushing or defending that couldn't happen at the time — are allowed to complete in slow, contained ways.",
    ],
    theory: [
      "The model proposes that threat mobilises large amounts of survival energy, and that when the response is thwarted, that activation stays incompletely discharged in the nervous system.",
      "It draws on observations of animals in the wild, on stress physiology, and on polyvagal-informed ideas about states of safety, mobilisation and shutdown.",
      "These are theoretical models, not settled neuroscience, and some elements remain contested in the research literature.",
    ],
    session: [
      "Sessions are conversational and slow, often 50–60 minutes, mostly seated.",
      "You may be asked what you notice in your body right now, and to stay with a sensation for a while.",
      "Trembling, temperature changes, deep breaths, yawning or subtle movement are treated as useful, not as problems.",
      "The therapist tracks your regulation closely and slows down or stops long before overwhelm.",
    ],
    techniques: [
      "Tracking body sensation",
      "Titration — working in very small amounts",
      "Pendulation between activation and settling",
      "Resourcing (internal and external supports)",
      "Orienting to the present environment",
      "Completing thwarted defensive movements",
    ],
    usedFor: [
      "Trauma held physically — chronic tension, freeze, shutdown, hypervigilance",
      "Developmental and relational trauma",
      "Medical, birth and accident trauma",
      "Sometimes used alongside treatment for chronic pain and functional symptoms",
    ],
    trauma: [
      "SE targets the physiological side of PTSD — the startle, the bracing, the numbness — which talk-based work can leave untouched.",
      "It is often chosen by people for whom cognitive or exposure approaches felt intolerable, or who cannot access memory verbally.",
    ],
    complexTrauma: [
      "The slow, dose-controlled pacing is well suited to complex trauma and to people who dissociate easily.",
      "Many people use SE as a stabilisation phase alongside or before memory-focused therapy.",
      "It is not a substitute for a guideline-recommended PTSD treatment if that is what you want and can access.",
    ],
    duration: [
      "Highly variable: some people work over 10–20 sessions, others continue for a year or more with developmental trauma.",
      "There is no fixed protocol length.",
    ],
    experience: [
      "Sessions can feel unusually slow, and 'nothing happening' is common early on.",
      "Physical release — shaking, warmth, tears, deep breaths, sudden tiredness — is common and usually settles.",
      "Some people notice change in sleep, startle or body tension before they notice change in thoughts.",
    ],
    benefits: [
      "Works with the nervous system directly",
      "Doesn't require retelling the trauma",
      "Gentle pacing suits dissociation and shutdown",
      "Often improves the felt sense of safety in the body",
    ],
    limitations: [
      "The research base is small compared with front-line PTSD treatments",
      "Practitioner training varies; SE training is a modality certification, not a licence to practise therapy",
      "Body-focused attention can itself be activating for some people",
      "Should not replace medical assessment of physical symptoms",
    ],
    evidenceBase: [
      "A small number of randomised and pilot studies report reductions in PTSD symptoms after SE, and a 2021 systematic review concluded the approach shows promise while calling the evidence preliminary.",
      "Trials are generally small, with limited long-term follow-up and few active comparisons.",
      "SE is not currently a guideline-recommended first-line PTSD treatment in NICE or APA guidance.",
      "It is best described as a complementary or adjunctive approach with emerging support.",
    ],
    whoProvides: [
      "Ideally a registered mental health professional or licensed bodyworker who has completed the three-year Somatic Experiencing International training (SEP).",
      "SEP certification alone does not mean someone is a registered psychotherapist — check both.",
    ],
    selfHelp: [
      "Gentle self-practices — orienting to the room, noticing sensation, slowing down, feeling your feet — are safe and useful.",
      "Deliberately approaching traumatic material through the body alone is not advised; that is where a trained practitioner matters.",
    ],
    questions: [
      "What is your core professional registration, alongside your SE training?",
      "How do you work with dissociation or shutdown if it happens in session?",
      "How will we know whether this is helping, and over what timeframe?",
      "Do you work alongside other trauma treatments?",
    ],
    resources: [
      { label: "Somatic tools", to: "/grounding", blurb: "Simple body-based ways to settle." },
      { label: "Polyvagal Ladder", to: "/learn/polyvagal-ladder", blurb: "States of mobilisation, shutdown and safety." },
      { label: "Window of Tolerance", to: "/learn/window-of-tolerance", blurb: "The range where processing is possible." },
    ],
    references: [
      {
        label: "Somatic Experiencing for PTSD: systematic review",
        source: "European Journal of Psychotraumatology / PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/34285758/",
      },
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
      {
        label: "Somatic Experiencing International",
        source: "SE International",
        url: "https://traumahealing.org/",
      },
    ],
  },

  {
    slug: "prolonged-exposure",
    name: "Prolonged Exposure Therapy",
    abbrev: "PE",
    badge: "Trauma Processing",
    group: "trauma-processing",
    tagline:
      "A structured PTSD therapy built on gradually approaching what trauma has made you avoid — both the memory itself and the safe situations you've stopped doing.",
    evidence: "established",
    evidenceSummary:
      "One of the most extensively researched PTSD treatments, strongly recommended in APA and VA/DoD guidelines.",
    whatIsIt: [
      "PE is a manualised behavioural therapy for PTSD, usually 8–15 sessions.",
      "It has two central components: imaginal exposure (recounting the memory in the present tense, repeatedly) and in-vivo exposure (approaching avoided but safe situations).",
      "Recordings of the imaginal exposure are usually listened to between sessions.",
    ],
    howItWorks: [
      "Avoidance is understood as the engine of PTSD: it prevents new learning, so the fear never updates.",
      "Repeated, controlled approach allows the nervous system to learn that the memory is a memory, and that the avoided supermarket, road or conversation is not itself dangerous.",
      "Distress typically reduces both within a session and across sessions — this pattern is tracked deliberately.",
    ],
    theory: [
      "PE developed from emotional processing theory: fear structures containing stimulus, response and meaning information need to be activated and then given corrective information.",
      "More recent framing draws on inhibitory learning — new, safer associations are built alongside the old ones rather than erasing them.",
    ],
    session: [
      "Sessions run around 90 minutes, weekly or twice-weekly.",
      "Early sessions cover psychoeducation, breathing retraining and building an in-vivo hierarchy.",
      "Middle sessions are dominated by imaginal exposure — typically 30–45 minutes of recounting — followed by processing what came up.",
      "Homework includes listening back to the recording and completing in-vivo exposure steps.",
    ],
    techniques: [
      "Psychoeducation about avoidance and PTSD",
      "Breathing retraining",
      "In-vivo exposure hierarchy",
      "Repeated imaginal exposure in the present tense",
      "Recorded exposure listened to between sessions",
      "Processing of hot spots — the most distressing moments",
    ],
    usedFor: [
      "PTSD in adults, extensively studied in veterans, assault survivors and accident survivors",
      "Trauma-related avoidance that has narrowed someone's life",
      "Adolescent PTSD, in adapted forms",
    ],
    trauma: [
      "PE is trauma-specific by design and is one of the treatments with the largest PTSD evidence base worldwide.",
      "It is often the fastest route out of severe avoidance, but it asks a lot in the short term.",
    ],
    complexTrauma: [
      "PE has been studied in people with childhood abuse histories and can work, though a preparation phase is often added.",
      "Where dissociation is significant, unmodified PE can be destabilising; skills-first models (such as STAIR followed by exposure) are commonly used instead.",
      "Discuss pacing honestly — a good PE therapist adjusts rather than pushes.",
    ],
    duration: [
      "Typically 8–15 sessions of about 90 minutes.",
      "Intensive formats compress this into a couple of weeks; complex presentations usually run longer.",
    ],
    experience: [
      "The first exposures are usually the hardest, and distress often rises before it falls.",
      "People frequently describe feeling exhausted after early sessions and noticeably freer after several weeks.",
      "Dropout rates are higher than for less demanding therapies, which is worth knowing in advance rather than discovering mid-course.",
    ],
    benefits: [
      "Large, consistently replicated reductions in PTSD symptoms",
      "Clear structure and measurable progress",
      "Directly reverses the avoidance that shrinks daily life",
      "Relatively brief",
    ],
    limitations: [
      "Demanding, and harder to tolerate than many alternatives",
      "Higher dropout in some trials",
      "Not suitable during ongoing danger, acute suicidality or untreated severe dissociation",
      "Requires a properly trained clinician; poorly delivered exposure can retraumatise",
    ],
    evidenceBase: [
      "PE is 'strongly recommended' for PTSD by the APA and by the VA/DoD guideline, based on dozens of randomised controlled trials.",
      "Effects are generally maintained at follow-up, and comparative trials find PE broadly equivalent to CPT and EMDR.",
      "Evidence is strongest in adults; complex-trauma-specific evidence is thinner.",
    ],
    whoProvides: [
      "Registered mental health professionals with formal PE training and supervision.",
      "In the UK, trauma-focused CBT delivered by a BABCP-accredited therapist is the usual equivalent route.",
    ],
    selfHelp: [
      "Gradual, self-paced approach to avoided everyday situations can be genuinely helpful and is a normal part of recovery.",
      "Repeatedly reliving a traumatic memory alone is not self-help and can make things worse — that component belongs in supported therapy.",
    ],
    questions: [
      "How will we pace exposure, and what happens if it's too much?",
      "What's your training in PE, and how many courses have you delivered?",
      "Will I need to record sessions, and how is that kept private?",
      "What support is there between sessions?",
    ],
    resources: [
      { label: "Grounding toolkit", to: "/grounding", blurb: "Anchor yourself before and after exposure work." },
      { label: "Breathing tools", to: "/breathing", blurb: "Breathing retraining is part of the protocol." },
      { label: "Trigger Journal", to: "/trigger-journal", blurb: "Map what you're avoiding, one item at a time." },
    ],
    references: [
      {
        label: "PTSD treatment guideline",
        source: "American Psychological Association",
        url: "https://www.apa.org/ptsd-guideline",
      },
      {
        label: "VA/DoD clinical practice guideline for PTSD",
        source: "US Department of Veterans Affairs",
        url: "https://www.healthquality.va.gov/guidelines/mh/ptsd/",
      },
      {
        label: "ISTSS prevention and treatment guidelines",
        source: "International Society for Traumatic Stress Studies",
        url: "https://istss.org/clinical-resources/treating-trauma/new-istss-prevention-and-treatment-guidelines",
      },
    ],
  },

  {
    slug: "act",
    name: "Acceptance & Commitment Therapy",
    abbrev: "ACT",
    badge: "Cognitive & Behavioural",
    group: "cognitive-behavioural",
    tagline:
      "Rather than trying to remove difficult thoughts and feelings, ACT builds the ability to hold them differently — and to keep moving towards what matters to you.",
    evidence: "broader",
    evidenceSummary:
      "A well-evidenced therapy across anxiety, depression, chronic pain and stress; for PTSD specifically it is supported but not a first-line trauma-processing treatment.",
    whatIsIt: [
      "ACT is a behavioural therapy that focuses on psychological flexibility: being present, open to experience, and guided by your values.",
      "It does not treat difficult thoughts as errors to be corrected, but as events to be noticed.",
      "The measure of progress is not how you feel, but whether your life is moving in a direction you actually want.",
    ],
    howItWorks: [
      "Six processes are worked through: acceptance, cognitive defusion, present-moment contact, self-as-context, values, and committed action.",
      "Defusion exercises create distance from thoughts — noticing 'I'm having the thought that I'm broken' rather than living inside it.",
      "Values work identifies what matters, and committed action turns that into small, concrete steps.",
    ],
    theory: [
      "ACT rests on relational frame theory, an account of how language links experiences together — which is why words alone can trigger old pain.",
      "Because you can't unlearn language, trying to eliminate distressing thoughts tends to amplify them; the alternative is changing your relationship to them.",
      "Experiential avoidance — the effort not to feel — is treated as the common thread across much human suffering.",
    ],
    session: [
      "Sessions are usually 50–60 minutes, weekly, and are often experiential rather than purely conversational.",
      "You may be asked to do brief mindfulness exercises, use metaphors, or physically act something out.",
      "Between-session practice is usually a values-based action rather than a worksheet.",
    ],
    techniques: [
      "Mindfulness and present-moment exercises",
      "Cognitive defusion (e.g. noticing thoughts as thoughts)",
      "Willingness and acceptance practices",
      "Values clarification",
      "Committed action and behavioural goals",
      "Metaphors and experiential exercises",
    ],
    usedFor: [
      "Anxiety, depression, stress and burnout",
      "Chronic pain and long-term health conditions",
      "Substance use and self-critical patterns",
      "Trauma-related avoidance, shame and disconnection from life",
    ],
    trauma: [
      "ACT addresses the avoidance and numbing side of trauma directly, and can be a good fit where the goal is getting life back rather than processing memories.",
      "It is sometimes used before or alongside trauma-focused therapy, and sometimes as an alternative where memory work isn't wanted or safe.",
    ],
    complexTrauma: [
      "The emphasis on self-compassion, values and workability suits complex trauma, where 'symptom removal' is rarely the whole picture.",
      "Acceptance language must be handled carefully: it never means accepting abuse, harm or unsafe situations.",
      "A trauma-informed ACT therapist will name that distinction explicitly.",
    ],
    duration: [
      "Commonly 8–16 sessions, though brief 4-session and longer open-ended formats both exist.",
    ],
    experience: [
      "People often find ACT counter-intuitive at first — it stops trying to make bad feelings go away.",
      "Metaphors and exercises can feel unusual; many people report they stay with them long afterwards.",
      "Change often shows up in behaviour before it shows up in mood.",
    ],
    benefits: [
      "Reduces the struggle with symptoms rather than adding another fight",
      "Values focus gives direction beyond symptom scores",
      "Transferable to many areas of life at once",
      "Compatible with most other therapies",
    ],
    limitations: [
      "Not a trauma memory-processing treatment",
      "The abstract, metaphor-heavy style doesn't suit everyone",
      "'Acceptance' can be misheard as resignation if poorly explained",
      "For PTSD specifically, the trial evidence is weaker than for EMDR, CPT, PE or TF-CBT",
    ],
    evidenceBase: [
      "ACT has hundreds of randomised trials across anxiety, depression, chronic pain, psychosis and workplace stress, with good overall support.",
      "For PTSD, trials are fewer and generally smaller; ACT appears helpful but is not listed as a first-line trauma treatment in NICE or APA guidance.",
      "Reviews often note that ACT performs comparably to traditional CBT for many conditions.",
    ],
    whoProvides: [
      "Registered psychologists, psychotherapists, counsellors and CBT therapists with ACT training.",
      "The Association for Contextual Behavioral Science lists practitioners; check core registration too.",
    ],
    selfHelp: [
      "ACT translates unusually well to self-help — evidence-based workbooks and app-based programmes exist and have research support.",
      "Values and defusion exercises are safe to practise alone.",
      "For PTSD, self-help ACT is best seen as a support alongside, not instead of, proper trauma treatment.",
    ],
    questions: [
      "How do you make sure acceptance work stays trauma-informed?",
      "Do you combine ACT with any trauma-focused treatment?",
      "What would progress look like for me in three months?",
      "How much practice between sessions is expected?",
    ],
    resources: [
      { label: "Grounding toolkit", to: "/grounding", blurb: "Present-moment practice, in one minute." },
      { label: "Recovery Journal", to: "/journal", blurb: "Track values-based steps and what got in the way." },
      { label: "Learn Hub: DBT", to: "/learn/dbt", blurb: "Related mindfulness and distress-tolerance skills." },
    ],
    references: [
      {
        label: "Association for Contextual Behavioral Science",
        source: "ACBS",
        url: "https://contextualscience.org/act",
      },
      {
        label: "ACT randomised controlled trial evidence overview",
        source: "PubMed",
        url: "https://pubmed.ncbi.nlm.nih.gov/?term=acceptance+and+commitment+therapy+meta-analysis",
      },
      { label: "PTSD guideline NG116", source: "NICE (UK)", url: "https://www.nice.org.uk/guidance/ng116" },
    ],
  },
];
